import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { BiContainer, Box, Column, FullPageModal, FullPageModalHeaderActions, Row, Tabs, Typography, useDevice, useOpenUnsavedChangesModal } from 'seagull';
import { Undo } from 'seagull/icons/arrows';
import { Checkmark, CloseCross } from 'seagull/icons/essentials';
import { NotificationHelpMeWriteDrawer } from '~/components/ai/HelpMeWriteDrawer/wirings/NotificationHelpMeWriteDrawer';
import { NotificationEditField } from '~/components/NotificationEditField/NotificationEditField';
import { NotificationEmailPreview } from '~/components/NotificationPreview/NotificationEmailPreview/NotificationEmailPreview';
import { SMS_VARIANT_KEY } from '~/components/NotificationPreview/NotificationEmailPreview/NotificationEmailPreview.utils';
import { NotificationSmsPreview } from '~/components/NotificationPreview/NotificationSmsPreview/NotificationSmsPreview';
import { useStore } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { NotificationSettingsEntryType } from '~/models/NotificationSettingsModel';
import { selectors } from '~/store/state-selectors';
import { useRevertNotificationSetting } from '../../hooks/useRevertNotificationSetting';
import { useUpdateNotificationSettingsV2 } from '../../hooks/useUpdateNotificationSettingsV2';
import { deriveIsActive, Tab } from '../../NotificationSettings.utils';
import { HelpMeWriteTrigger } from './components/HelpMeWriteTrigger/HelpMeWriteTrigger';
import { NotifyRow } from './components/NotifyRow';
import styles from './NotificationEditModal.module.scss';
import { GetNotificationEditModalTID } from './NotificationEditModal.testIds';
import { decodeUnknownTemplateVariables, deriveOrderedVariantKeys, EMPTY_VARIANT, isDraftValid, resolveErrorText, resolveVariantLabel, seedVariants, UNKNOWN_TEMPLATE_VARIABLES_PREFIX, validateDraft } from './NotificationEditModal.utils';
const EMPTY_VARIANTS = {};
export const NotificationEditModal = (param)=>{
    let { notificationName, notification, notificationLabel, selectedTab, onClose, onRevertSuccess, testId = 'notification-edit-modal' } = param;
    var _notification_variants, _ref, _ref1, _form_watch, _form_watch1, _ref2, _resolveErrorText, _ref3, _ref4, _ref5, _notification_variants_activeVariantKey, _ref6;
    var _form_formState_errors_variants, _variantFieldErrors_subject, _variantFieldErrors_message, _form_formState_errors_recipientTypes, _variantFieldErrors_root, _notification_variants_activeVariantKey1, _notification_variants_activeVariantKey2, _notification_variants_activeVariantKey3, _notification_variants_activeVariantKey4;
    const { NotificationEditModalTestIds: testIds } = useMemo(()=>GetNotificationEditModalTID(testId), [
        testId
    ]);
    const { isMobileDevice } = useDevice();
    const { HELP_ME_WRITE_NOTIFICATIONS } = useFeatureFlags();
    const aiEnabled = HELP_ME_WRITE_NOTIFICATIONS === true && !isMobileDevice;
    const { t: contentTexts } = useLocaleKeys('pages.notificationSettings.contentCustomization');
    const { t: pageTexts } = useLocaleKeys('pages.notificationSettings');
    const variantLabelOf = useCallback((key)=>resolveVariantLabel(notificationName, key, contentTexts.variantLabels), [
        notificationName,
        contentTexts
    ]);
    const businessId = useStore(selectors.user.businessId);
    const businessLogoUrl = useStore(selectors.user.businessLogo);
    const businessName = useStore(selectors.user.businessName);
    const branding = useMemo(()=>selectedTab === Tab.Client ? {
            type: 'business',
            name: businessName !== null && businessName !== void 0 ? businessName : '',
            logoUrl: businessLogoUrl !== null && businessLogoUrl !== void 0 ? businessLogoUrl : undefined
        } : {
            type: 'anchor'
        }, [
        selectedTab,
        businessName,
        businessLogoUrl
    ]);
    const initialVariants = (_notification_variants = notification.variants) !== null && _notification_variants !== void 0 ? _notification_variants : EMPTY_VARIANTS;
    const [variantKeys, setVariantKeys] = useState(()=>deriveOrderedVariantKeys(initialVariants, variantLabelOf));
    const initialVariantKey = variantKeys[0];
    const [activeVariantKey, setActiveVariantKey] = useState(initialVariantKey);
    const activeVariantIsSms = activeVariantKey.toLowerCase() === SMS_VARIANT_KEY;
    const [hasVendorOverrides, setHasVendorOverrides] = useState(notification.hasVendorOverrides);
    const [isRefreshingFromRevert, setIsRefreshingFromRevert] = useState(false);
    const [isHelpMeWriteOpen, setIsHelpMeWriteOpen] = useState(false);
    const [helpMeWriteResetKey, setHelpMeWriteResetKey] = useState(0);
    const contentEditable = notification.editability.content;
    useEffect(()=>{
        setIsHelpMeWriteOpen(false);
        setHelpMeWriteResetKey((key)=>key + 1);
    }, [
        activeVariantKey
    ]);
    useEffect(()=>{
        if (!contentEditable && isHelpMeWriteOpen) {
            setIsHelpMeWriteOpen(false);
            setHelpMeWriteResetKey((key)=>key + 1);
        }
    }, [
        contentEditable,
        isHelpMeWriteOpen
    ]);
    const initialRecurring = notification.type === NotificationSettingsEntryType.Recurring ? notification.recurring : null;
    const initialMinutes = (_ref = initialRecurring === null || initialRecurring === void 0 ? void 0 : initialRecurring.minutes) !== null && _ref !== void 0 ? _ref : null;
    const initialTimes = (_ref1 = initialRecurring === null || initialRecurring === void 0 ? void 0 : initialRecurring.times) !== null && _ref1 !== void 0 ? _ref1 : null;
    const allowedSystemKeysFor = useCallback((key)=>{
        var _ref;
        var _notification_variants_key;
        return new Set(Object.keys((_ref = (_notification_variants_key = notification.variants[key]) === null || _notification_variants_key === void 0 ? void 0 : _notification_variants_key.templateVariables) !== null && _ref !== void 0 ? _ref : {}));
    }, [
        notification.variants
    ]);
    const resolver = useCallback((values)=>{
        const variantErrors = {};
        for (const key of variantKeys){
            var _values_variants_key;
            const variant = (_values_variants_key = values.variants[key]) !== null && _values_variants_key !== void 0 ? _values_variants_key : EMPTY_VARIANT;
            const skipSubject = key.toLowerCase() === SMS_VARIANT_KEY;
            const draftErrors = validateDraft(variant.subject, variant.message, allowedSystemKeysFor(key), {
                skipSubject
            });
            if (isDraftValid(draftErrors)) continue;
            const entry = {};
            if (draftErrors.subject != null) {
                entry.subject = {
                    type: 'validate',
                    message: draftErrors.subject
                };
            }
            if (draftErrors.message != null) {
                entry.message = {
                    type: 'validate',
                    message: draftErrors.message
                };
            }
            if (draftErrors.unknownTemplateVariables != null && draftErrors.unknownTemplateVariables.length > 0) {
                entry.root = {
                    type: 'validate',
                    message: "".concat(UNKNOWN_TEMPLATE_VARIABLES_PREFIX).concat(JSON.stringify(draftErrors.unknownTemplateVariables))
                };
            }
            variantErrors[key] = entry;
        }
        const fieldErrors = {};
        if (Object.keys(variantErrors).length > 0) {
            fieldErrors.variants = variantErrors;
        }
        const requiresRecipients = selectedTab === Tab.Vendor && notification.editability.recipients;
        if (requiresRecipients && values.recipientTypes.length === 0) {
            fieldErrors.recipientTypes = {
                type: 'validate',
                message: 'NOTIFICATION_V2_RECIPIENTS_EMPTY'
            };
        }
        const hasErrors = Object.keys(fieldErrors).length > 0;
        return {
            values: hasErrors ? {} : values,
            errors: fieldErrors
        };
    }, [
        variantKeys,
        selectedTab,
        notification.editability.recipients,
        allowedSystemKeysFor
    ]);
    const form = useForm({
        mode: 'onSubmit',
        resolver,
        defaultValues: {
            variants: seedVariants(initialVariants, variantKeys),
            recipientTypes: notification.recipientTypes,
            minutes: initialMinutes,
            times: initialTimes
        }
    });
    const subjectFieldName = "variants.".concat(activeVariantKey, ".subject");
    const messageFieldName = "variants.".concat(activeVariantKey, ".message");
    const subjectValue = (_form_watch = form.watch(subjectFieldName)) !== null && _form_watch !== void 0 ? _form_watch : '';
    const messageValue = (_form_watch1 = form.watch(messageFieldName)) !== null && _form_watch1 !== void 0 ? _form_watch1 : '';
    const watchedRecipientTypes = form.watch('recipientTypes');
    const watchedMinutes = form.watch('minutes');
    const watchedTimes = form.watch('times');
    // RHF only tracks isDirty/isSubmitted if subscribed via render-time read.
    const { isDirty, isSubmitted } = form.formState;
    const pickerAllowedSystemKeys = useMemo(()=>allowedSystemKeysFor(activeVariantKey), [
        allowedSystemKeysFor,
        activeVariantKey
    ]);
    const openUnsavedChangesModal = useOpenUnsavedChangesModal();
    const { update, isLoading: isSaving, error: saveError } = useUpdateNotificationSettingsV2({
        onSuccess: ()=>onClose()
    });
    const resetEditorFromNotification = (fresh)=>{
        var _fresh_variants, _ref, _ref1;
        const freshVariants = (_fresh_variants = fresh.variants) !== null && _fresh_variants !== void 0 ? _fresh_variants : EMPTY_VARIANTS;
        const freshKeys = deriveOrderedVariantKeys(freshVariants, variantLabelOf);
        const freshRecurring = fresh.type === NotificationSettingsEntryType.Recurring ? fresh.recurring : null;
        const freshMinutes = (_ref = freshRecurring === null || freshRecurring === void 0 ? void 0 : freshRecurring.minutes) !== null && _ref !== void 0 ? _ref : null;
        const freshTimes = (_ref1 = freshRecurring === null || freshRecurring === void 0 ? void 0 : freshRecurring.times) !== null && _ref1 !== void 0 ? _ref1 : null;
        setVariantKeys(freshKeys);
        if (!freshKeys.includes(activeVariantKey)) setActiveVariantKey(freshKeys[0]);
        setHasVendorOverrides(fresh.hasVendorOverrides);
        form.reset({
            variants: seedVariants(freshVariants, freshKeys),
            recipientTypes: fresh.recipientTypes,
            minutes: freshMinutes,
            times: freshTimes
        });
    };
    const handleRevertSuccess = async ()=>{
        if (onRevertSuccess == null) return;
        setIsRefreshingFromRevert(true);
        try {
            const fresh = await onRevertSuccess();
            // The override was cleared on the server but we have no fresh state to populate the
            // editor with. Bail out to the parent so it can re-fetch, rather than leaving the
            // user looking at stale overrides with the Revert button still visible.
            if (fresh == null) onClose();
            else resetEditorFromNotification(fresh);
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    source: 'notification-edit-modal-revert-refresh'
                }
            });
            onClose();
        } finally{
            setIsRefreshingFromRevert(false);
        }
    };
    const { revert, isLoading: isReverting } = useRevertNotificationSetting({
        onSuccess: ()=>{
            void handleRevertSuccess();
        }
    });
    const onValid = (values)=>{
        if (businessId == null) return;
        const variantPayload = {};
        for (const key of variantKeys){
            var _values_variants_key;
            const variant = (_values_variants_key = values.variants[key]) !== null && _values_variants_key !== void 0 ? _values_variants_key : EMPTY_VARIANT;
            variantPayload[key] = {
                subject: variant.subject.trim(),
                message: variant.message.trim()
            };
        }
        // Modal saves never touch the toggle state — that lives behind a separate endpoint —
        // so editing recipients (or content) here cannot accidentally flip an off toggle on.
        const baseUpdate = notification.type === NotificationSettingsEntryType.Recurring ? {
            type: NotificationSettingsEntryType.Recurring,
            recipientTypes: values.recipientTypes,
            recurring: {
                minutes: values.minutes,
                times: values.times
            }
        } : {
            type: NotificationSettingsEntryType.Single,
            recipientTypes: values.recipientTypes
        };
        update({
            businessId,
            notificationName,
            data: _object_spread_props(_object_spread({}, baseUpdate), {
                variants: variantPayload
            })
        });
    };
    const onInvalid = (errs)=>{
        const firstBadVariantKey = variantKeys.find((k)=>{
            var _errs_variants;
            return ((_errs_variants = errs.variants) === null || _errs_variants === void 0 ? void 0 : _errs_variants[k]) != null;
        });
        if (firstBadVariantKey != null && firstBadVariantKey !== activeVariantKey) {
            setActiveVariantKey(firstBadVariantKey);
        }
    };
    const handleSave = form.handleSubmit(onValid, onInvalid);
    const handleRevert = async ()=>{
        if (!canRevert) return;
        if (businessId == null) return;
        const confirmed = await openUnsavedChangesModal({
            title: contentTexts.revertConfirmTitle(),
            content: contentTexts.revertConfirmMessage(),
            primaryButtonLabel: contentTexts.revertConfirmConfirm(),
            secondaryButtonLabel: contentTexts.revertConfirmCancel()
        });
        if (!confirmed) return;
        revert({
            businessId,
            notificationName
        });
    };
    const handleCancel = async ()=>{
        if (isDirty) {
            const confirmed = await openUnsavedChangesModal({
                title: contentTexts.discardChangesTitle(),
                content: contentTexts.discardChangesMessage(),
                primaryButtonLabel: contentTexts.discardChangesConfirm(),
                secondaryButtonLabel: contentTexts.discardChangesKeep()
            });
            if (!confirmed) return;
        }
        onClose();
    };
    // Since ANCR-908 recipients survive toggle-off, so Active requires toggle on AND at least one recipient.
    const isActive = notification.editability.recipients === false || deriveIsActive(notification) && watchedRecipientTypes.length > 0;
    const canRevert = hasVendorOverrides;
    const variantFieldErrors = (_form_formState_errors_variants = form.formState.errors.variants) === null || _form_formState_errors_variants === void 0 ? void 0 : _form_formState_errors_variants[activeVariantKey];
    const subjectErrorCode = variantFieldErrors === null || variantFieldErrors === void 0 ? void 0 : (_variantFieldErrors_subject = variantFieldErrors.subject) === null || _variantFieldErrors_subject === void 0 ? void 0 : _variantFieldErrors_subject.message;
    const messageErrorCode = variantFieldErrors === null || variantFieldErrors === void 0 ? void 0 : (_variantFieldErrors_message = variantFieldErrors.message) === null || _variantFieldErrors_message === void 0 ? void 0 : _variantFieldErrors_message.message;
    const recipientsErrorCode = (_form_formState_errors_recipientTypes = form.formState.errors.recipientTypes) === null || _form_formState_errors_recipientTypes === void 0 ? void 0 : _form_formState_errors_recipientTypes.message;
    const saveErrorCode = saveError === null || saveError === void 0 ? void 0 : saveError.code;
    const inlineErrorKey = (_ref2 = subjectErrorCode !== null && subjectErrorCode !== void 0 ? subjectErrorCode : messageErrorCode) !== null && _ref2 !== void 0 ? _ref2 : saveErrorCode;
    const variantUnknownTemplateVariables = decodeUnknownTemplateVariables(variantFieldErrors === null || variantFieldErrors === void 0 ? void 0 : (_variantFieldErrors_root = variantFieldErrors.root) === null || _variantFieldErrors_root === void 0 ? void 0 : _variantFieldErrors_root.message);
    return /*#__PURE__*/ _jsxs(FullPageModal, {
        contentSkin: "light",
        contentClassName: styles.modalContent,
        headerClassName: styles.modalHeader,
        testId: testId,
        onClose: handleCancel,
        children: [
            /*#__PURE__*/ _jsx(FullPageModal.HeaderStart, {
                children: /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    style: {
                        minWidth: 0
                    },
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            weight: "normal",
                            className: styles.title,
                            testId: testIds.Title(),
                            children: "".concat(contentTexts.modalTitle(), " - ").concat(notificationLabel)
                        }),
                        !isMobileDevice && /*#__PURE__*/ _jsx(Box, {
                            as: "span",
                            inline: true,
                            className: cn(styles.statusBadge, isActive ? styles.active : styles.inactive),
                            testId: testIds.StatusBadge(),
                            children: isActive ? pageTexts.statusActive() : pageTexts.statusInactive()
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(FullPageModalHeaderActions, {
                testId: testId,
                mainAction: {
                    text: contentTexts.saveButton(),
                    icon: /*#__PURE__*/ _jsx(Checkmark, {
                        size: 16
                    }),
                    onClick: handleSave,
                    loading: isSaving,
                    testId: testIds.SaveButton(),
                    bi: {
                        name: 'save'
                    }
                },
                secondaryAction: {
                    text: contentTexts.cancelButton(),
                    icon: /*#__PURE__*/ _jsx(CloseCross, {
                        size: 16
                    }),
                    onClick: handleCancel,
                    testId: testIds.CancelButton(),
                    bi: {
                        name: 'cancel'
                    }
                },
                additionalActions: {
                    inline: [
                        {
                            text: contentTexts.revertButton(),
                            iconStart: /*#__PURE__*/ _jsx(Undo, {
                                size: 20
                            }),
                            onClick: handleRevert,
                            loading: isReverting || isRefreshingFromRevert,
                            disabled: !canRevert,
                            skin: 'dark',
                            tooltipTitle: !canRevert ? contentTexts.revertButtonDisabledTooltip() : undefined,
                            testId: testIds.RevertButton(),
                            bi: {
                                name: 'revert_to_default'
                            }
                        }
                    ]
                }
            }),
            /*#__PURE__*/ _jsxs(BiContainer, {
                name: "edit_notification",
                children: [
                    variantKeys.length > 1 && /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.stickyTabs, {
                            [styles.stickyTabsMobile]: isMobileDevice
                        }),
                        "data-testid": testIds.StickyTabsContainer(),
                        children: /*#__PURE__*/ _jsx(Tabs, {
                            bi: {
                                name: 'notification_edit_variant_tabs'
                            },
                            value: activeVariantKey,
                            onChange: (_event, nextValue)=>{
                                setActiveVariantKey(nextValue);
                                form.clearErrors();
                            },
                            testId: testIds.VariantTabs(),
                            tabs: variantKeys.map((key)=>({
                                    id: key,
                                    value: key,
                                    component: resolveVariantLabel(notificationName, key, contentTexts.variantLabels),
                                    testId: testIds.VariantTab(key)
                                }))
                        })
                    }),
                    /*#__PURE__*/ _jsx(Column, {
                        gap: isMobileDevice ? 16 : 24,
                        padding: isMobileDevice ? 16 : 32,
                        testId: testIds.Root(),
                        className: cn(styles.card, {
                            [styles.cardMobile]: isMobileDevice
                        }),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: cn(styles.bodyGrid, {
                                [styles.bodyGridMobile]: isMobileDevice
                            }),
                            "data-testid": testIds.BodyGrid(),
                            children: [
                                /*#__PURE__*/ _jsxs(Column, {
                                    gap: 16,
                                    className: styles.contentColumn,
                                    children: [
                                        /*#__PURE__*/ _jsxs(Row, {
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            children: [
                                                /*#__PURE__*/ _jsx(Typography, {
                                                    variant: "paragraph1",
                                                    weight: "normal",
                                                    testId: testIds.ContentSectionHeading(),
                                                    children: activeVariantIsSms ? contentTexts.smsContentSectionHeading() : contentTexts.emailContentSectionHeading()
                                                }),
                                                aiEnabled && contentEditable && /*#__PURE__*/ _jsx(HelpMeWriteTrigger, {
                                                    onClick: ()=>setIsHelpMeWriteOpen((prev)=>!prev)
                                                })
                                            ]
                                        }),
                                        !activeVariantIsSms && /*#__PURE__*/ _jsxs(Column, {
                                            gap: 4,
                                            className: styles.fieldContainer,
                                            testId: testIds.SubjectFieldContainer(),
                                            children: [
                                                /*#__PURE__*/ _jsx(Controller, {
                                                    control: form.control,
                                                    name: subjectFieldName,
                                                    render: (param)=>{
                                                        let { field } = param;
                                                        var _field_value;
                                                        return /*#__PURE__*/ _jsx(NotificationEditField, {
                                                            label: contentTexts.subjectLabel(),
                                                            value: (_field_value = field.value) !== null && _field_value !== void 0 ? _field_value : '',
                                                            onChange: field.onChange,
                                                            placeholder: contentTexts.subjectPlaceholder(),
                                                            error: subjectErrorCode != null,
                                                            disabled: !contentEditable || isHelpMeWriteOpen,
                                                            showAdornmentWhenDisabled: isHelpMeWriteOpen,
                                                            bi: {
                                                                name: 'subject'
                                                            },
                                                            testId: testIds.SubjectField(),
                                                            allowedSystemKeys: pickerAllowedSystemKeys,
                                                            disableCustomTags: true,
                                                            validateInBottomSheet: (value)=>{
                                                                var _form_getValues;
                                                                const code = validateDraft(value, (_form_getValues = form.getValues(messageFieldName)) !== null && _form_getValues !== void 0 ? _form_getValues : '').subject;
                                                                return {
                                                                    error: code != null,
                                                                    helperText: resolveErrorText(contentTexts.errors, code)
                                                                };
                                                            }
                                                        });
                                                    }
                                                }, subjectFieldName),
                                                subjectErrorCode != null && /*#__PURE__*/ _jsx(Typography, {
                                                    skin: "error",
                                                    variant: "paragraph3",
                                                    testId: testIds.SubjectError(),
                                                    children: resolveErrorText(contentTexts.errors, subjectErrorCode)
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(Column, {
                                            gap: 4,
                                            className: styles.fieldContainer,
                                            testId: testIds.MessageFieldContainer(),
                                            children: [
                                                /*#__PURE__*/ _jsx(Controller, {
                                                    control: form.control,
                                                    name: messageFieldName,
                                                    render: (param)=>{
                                                        let { field } = param;
                                                        var _field_value;
                                                        return /*#__PURE__*/ _jsx(NotificationEditField, {
                                                            multiline: true,
                                                            minRows: 6,
                                                            label: contentTexts.messageLabel(),
                                                            value: (_field_value = field.value) !== null && _field_value !== void 0 ? _field_value : '',
                                                            onChange: field.onChange,
                                                            placeholder: contentTexts.messagePlaceholder(),
                                                            error: messageErrorCode != null,
                                                            disabled: !contentEditable || isHelpMeWriteOpen,
                                                            showAdornmentWhenDisabled: isHelpMeWriteOpen,
                                                            bi: {
                                                                name: 'message'
                                                            },
                                                            testId: testIds.MessageField(),
                                                            adornmentClassName: styles.messageAdornment,
                                                            allowedSystemKeys: pickerAllowedSystemKeys,
                                                            disableCustomTags: true,
                                                            validateInBottomSheet: (value)=>{
                                                                var _form_getValues;
                                                                const code = validateDraft((_form_getValues = form.getValues(subjectFieldName)) !== null && _form_getValues !== void 0 ? _form_getValues : '', value, undefined, {
                                                                    skipSubject: activeVariantIsSms
                                                                }).message;
                                                                return {
                                                                    error: code != null,
                                                                    helperText: resolveErrorText(contentTexts.errors, code)
                                                                };
                                                            }
                                                        });
                                                    }
                                                }, messageFieldName),
                                                messageErrorCode != null && /*#__PURE__*/ _jsx(Typography, {
                                                    skin: "error",
                                                    variant: "paragraph3",
                                                    testId: testIds.MessageError(),
                                                    children: resolveErrorText(contentTexts.errors, messageErrorCode)
                                                })
                                            ]
                                        }),
                                        variantUnknownTemplateVariables.length > 0 && /*#__PURE__*/ _jsx(Typography, {
                                            skin: "error",
                                            variant: "paragraph3",
                                            testId: testIds.UnknownTemplateVariablesError(),
                                            children: contentTexts.errors.NOTIFICATION_V2_UNKNOWN_TEMPLATE_VARIABLES({
                                                templateVariables: variantUnknownTemplateVariables.map((variable)=>"{{".concat(variable, "}}")).join(', ')
                                            })
                                        }),
                                        inlineErrorKey != null && inlineErrorKey !== subjectErrorCode && inlineErrorKey !== messageErrorCode && /*#__PURE__*/ _jsx(Typography, {
                                            skin: "error",
                                            variant: "paragraph3",
                                            testId: testIds.InlineError(),
                                            children: (_resolveErrorText = resolveErrorText(contentTexts.errors, inlineErrorKey)) !== null && _resolveErrorText !== void 0 ? _resolveErrorText : contentTexts.errors.default()
                                        }),
                                        /*#__PURE__*/ _jsx(NotifyRow, {
                                            selectedTab: selectedTab,
                                            notificationType: notification.type,
                                            notificationName: notificationName,
                                            recipientTypes: watchedRecipientTypes,
                                            recipientTypeOptions: notification.recipientTypeOptions,
                                            recipientsEditable: notification.editability.recipients,
                                            recipientsError: recipientsErrorCode != null,
                                            minutes: watchedMinutes,
                                            times: watchedTimes,
                                            onChangeRecipients: (next)=>{
                                                form.setValue('recipientTypes', next, {
                                                    shouldDirty: true,
                                                    shouldValidate: isSubmitted
                                                });
                                            },
                                            onChangeMinutes: (next)=>{
                                                form.setValue('minutes', next, {
                                                    shouldDirty: true,
                                                    shouldValidate: isSubmitted
                                                });
                                            },
                                            onChangeTimes: (next)=>{
                                                form.setValue('times', next, {
                                                    shouldDirty: true,
                                                    shouldValidate: isSubmitted
                                                });
                                            }
                                        }),
                                        recipientsErrorCode != null && /*#__PURE__*/ _jsx(Typography, {
                                            skin: "error",
                                            variant: "paragraph3",
                                            testId: testIds.RecipientsError(),
                                            children: resolveErrorText(contentTexts.errors, recipientsErrorCode)
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Column, {
                                    gap: 12,
                                    alignItems: activeVariantIsSms ? 'center' : undefined,
                                    testId: testIds.PreviewRoot(),
                                    className: styles.previewColumn,
                                    children: activeVariantIsSms ? /*#__PURE__*/ _jsx(NotificationSmsPreview, {
                                        message: messageValue,
                                        templateVariables: (_ref3 = (_notification_variants_activeVariantKey1 = notification.variants[activeVariantKey]) === null || _notification_variants_activeVariantKey1 === void 0 ? void 0 : _notification_variants_activeVariantKey1.templateVariables) !== null && _ref3 !== void 0 ? _ref3 : {},
                                        vendorBusinessName: businessName !== null && businessName !== void 0 ? businessName : ''
                                    }) : /*#__PURE__*/ _jsx(NotificationEmailPreview, {
                                        notificationName: notificationName,
                                        subject: subjectValue,
                                        message: messageValue,
                                        templateVariables: (_ref4 = (_notification_variants_activeVariantKey2 = notification.variants[activeVariantKey]) === null || _notification_variants_activeVariantKey2 === void 0 ? void 0 : _notification_variants_activeVariantKey2.templateVariables) !== null && _ref4 !== void 0 ? _ref4 : {},
                                        ctaButtonText: (_ref5 = (_notification_variants_activeVariantKey3 = notification.variants[activeVariantKey]) === null || _notification_variants_activeVariantKey3 === void 0 ? void 0 : _notification_variants_activeVariantKey3.ctaButtonText) !== null && _ref5 !== void 0 ? _ref5 : '',
                                        branding: branding,
                                        vendorBusinessName: businessName !== null && businessName !== void 0 ? businessName : ''
                                    })
                                })
                            ]
                        })
                    })
                ]
            }),
            aiEnabled && contentEditable && /*#__PURE__*/ _jsx(NotificationHelpMeWriteDrawer, {
                open: isHelpMeWriteOpen,
                drawerWidth: 572,
                onClose: ()=>setIsHelpMeWriteOpen(false),
                notificationName: notificationName,
                variantKey: activeVariantKey,
                recipientParty: selectedTab === Tab.Vendor ? 'vendor' : 'customer',
                activeVariant: _object_spread_props(_object_spread({}, (_notification_variants_activeVariantKey = notification.variants[activeVariantKey]) !== null && _notification_variants_activeVariantKey !== void 0 ? _notification_variants_activeVariantKey : EMPTY_VARIANT), {
                    subject: subjectValue,
                    message: messageValue
                }),
                ctaButtonText: (_ref6 = (_notification_variants_activeVariantKey4 = notification.variants[activeVariantKey]) === null || _notification_variants_activeVariantKey4 === void 0 ? void 0 : _notification_variants_activeVariantKey4.ctaButtonText) !== null && _ref6 !== void 0 ? _ref6 : '',
                onAccept: (output)=>{
                    if (output.channel === 'email') {
                        form.setValue(subjectFieldName, output.subject, {
                            shouldDirty: true
                        });
                        form.setValue(messageFieldName, output.message, {
                            shouldDirty: true
                        });
                    } else {
                        form.setValue(messageFieldName, output.message, {
                            shouldDirty: true
                        });
                    }
                    setIsHelpMeWriteOpen(false);
                }
            }, helpMeWriteResetKey)
        ]
    });
};
